import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { ComponentFieldDefinition, WebsitePropertyOption } from '@troia-v3/types';
import { api } from '@/lib/api';
import { FormField } from '@/shared/components/ui/FormField';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import type { SearchableSelectOption } from '@/shared/components/ui/SearchableSelect';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';

interface PropertyReferenceSelectorProps {
  field: ComponentFieldDefinition;
  value?: string;
  onChange: (value: unknown) => void;
}

const toOption = (p: WebsitePropertyOption): SearchableSelectOption => {
  const sublabel = [p.code, p.city].filter(Boolean).join(' · ');
  return { id: p.id, label: p.label, ...(sublabel ? { sublabel } : {}) };
};

/**
 * Seletor de imóvel com busca no servidor (a base pode ter milhares de imóveis,
 * então não dá para listar tudo como o ReferenceSelector genérico faz).
 */
export const PropertyReferenceSelector: React.FC<PropertyReferenceSelectorProps> = ({
  field,
  value,
  onChange,
}) => {
  const { id: websiteId } = useParams<{ id: string }>();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const search = useDebouncedValue(term.trim(), 300);

  const { data: options = [], isLoading, isError: optionsError } = useQuery({
    queryKey: ['website-property-options', websiteId, search],
    queryFn: async () => {
      const response = await api.get(`websites/${websiteId}/property-options`, {
        params: search ? { search } : {},
      });
      return (response.data.data || []) as WebsitePropertyOption[];
    },
    enabled: open && !!websiteId,
    staleTime: 30 * 1000,
  });

  // Rótulo do imóvel já escolhido — a lista aberta pode não conter ele.
  // `null` = a consulta respondeu sem o imóvel (excluído ou fora dos bancos do site);
  // `undefined` = ainda carregando. Sem essa distinção o botão ficava em
  // "Carregando..." para sempre.
  const { data: current, isError: currentError } = useQuery({
    queryKey: ['website-property-options', websiteId, 'ids', value],
    queryFn: async () => {
      const response = await api.get(`websites/${websiteId}/property-options`, {
        params: { ids: value },
      });
      return ((response.data.data || []) as WebsitePropertyOption[])[0] ?? null;
    },
    enabled: !!value && !!websiteId,
    staleTime: 5 * 60 * 1000,
  });

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setTerm('');
  };

  const currentLabel = (): string => {
    if (current) return current.label;
    if (currentError) return 'Não foi possível carregar o imóvel';
    if (current === null) return 'Imóvel indisponível';
    return 'Carregando...';
  };
  const buttonLabel = value ? currentLabel() : `Selecione ${field.label}...`;

  return (
    <FormField label={field.label} description={field.description} required={field.required}>
      <div className="flex items-center gap-2">
        <button
          ref={anchorRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 min-w-0 text-left truncate rounded-lg px-3 py-2 text-sm"
          style={{
            backgroundColor: '#ffffff',
            color: value ? '#1e293b' : '#94a3b8',
            border: '1px solid rgba(0,0,0,0.10)',
          }}
          data-testid="property-ref-trigger"
        >
          {buttonLabel}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="px-2 py-1 rounded-md text-xs"
            style={{ color: '#64748b' }}
            data-testid="property-ref-clear"
          >
            Limpar
          </button>
        )}
      </div>

      <SearchableSelect
        open={open}
        onOpenChange={handleOpenChange}
        anchorRef={anchorRef}
        options={options.map(toOption)}
        selectedId={value ?? null}
        onSelect={(id) => onChange(id)}
        searchTerm={term}
        onSearchTermChange={setTerm}
        isLoading={isLoading}
        searchPlaceholder="Buscar imóvel por título ou código"
        emptyLabel={
          optionsError
            ? 'Não foi possível carregar os imóveis'
            : search
              ? 'Nenhum imóvel encontrado'
              : 'Vincule um banco de imóveis ao site'
        }
        testIdPrefix="property-ref"
      />
    </FormField>
  );
};
